import { take } from "rxjs/operators";
import { MoteService, ProjectService, IconService, MapService, FetchService } from "@motes-sdk/providers";
import { ObservableValue, ResourceState, Mote, Media, Mutex, Project, IconSet, MediaType, MediaFileType } from "@motes-sdk/models";
import { Observer } from "rxjs";

export interface SaveResourceService {
    saveResource(req: string, rsp: Response, cacheName?: string);
}

export class OfflineResources {
    private mapMutex: Mutex = new Mutex();

    constructor(
        private fetchService: FetchService,
        private projectService: ProjectService,
        private moteService: MoteService,
        private mapService: MapService,
        private iconService: IconService,
        private offlineService: SaveResourceService
    ) {
    }

    public async genResourceList(projectKey: string, abortCtrl: AbortController): Promise<ObservableValue<ResourceState>[]> {
        let resourceList: ObservableValue<ResourceState>[] = [];

        // Add this collection to download queue
        let motes = await this.moteService.watchProjectMotes([projectKey]).pipe(take(1)).toPromise();
        // Get media resources of all worlds inc. non published (ie. switzerland / detectaarhus)
        let allWorlds = await this.projectService.watchAllProjects().pipe(take(1)).toPromise();
        // Cache all published tours db requests
        let tours = await this.moteService.watchPublishedTourMotes().pipe(take(1)).toPromise();

        // Make sure that public world firebase request is cached as well
        await this.projectService.watchPublishedProjects().pipe(take(1)).toPromise();

        // Filter out published and offline enabled worlds (ensure that the list does not get too bloated)
        let worlds = allWorlds.filter(world => (world.published || world.allowOfflineMode));

        // Load icon set sprite sheet in cache
        await this.iconService.loadSpriteSheetCanvas();

        // Gen lazy loading map tile requests from all motes in project
        let tileRequests = this.genTileRequests(motes);
        // Gen lazy media requests from all motes in project
        let mediaRequests = this.genMediaRequests(motes, abortCtrl.signal, projectKey);
        // Gen lazy media requests from all tours (but only obtain image thumb and hd)
        let thumbRequests = this.genMediaRequests(tours, abortCtrl.signal, projectKey, MediaFileType.THUMB);
        let hdRequests = this.genMediaRequests(tours, abortCtrl.signal, projectKey, MediaFileType.HD);
        // Gen lazy world media requests
        let worldRequests = this.genWorldRequests(worlds, projectKey, abortCtrl.signal);
        // Gen individual mote requests
        let moteRequests = this.genMoteRequests(motes, projectKey);

        resourceList.push(...tileRequests);
        resourceList.push(...mediaRequests);
        resourceList.push(...thumbRequests);
        resourceList.push(...hdRequests);
        resourceList.push(...worldRequests);
        resourceList.push(...moteRequests);

        // Add empty resource if no motes in this project
        if(resourceList.length <= 0) resourceList.push(this.genEmptyRequest(projectKey));

        return resourceList;
    }

    private genWorldRequests(worlds: Project[], projectKey: string, abortSignal: AbortSignal): ObservableValue<ResourceState>[] {
        let requests = [];
        worlds.forEach(world => {
            let resources = this.genWorldResources(world, world.image, projectKey);

            let mediaRequests = resources.map(resource => {
                // Create a lazy observable fetch request for the resource
                return this.fetchService.fetchResource(resource, abortSignal, (rsp, res) => this.offlineService.saveResource(res.url, rsp));
            });
            requests.push(...mediaRequests);
        });
        return requests;
    }

    private genMoteRequests(motes: Mote[], projectKey: string): ObservableValue<ResourceState>[] {
        return motes.map(mote => {
            // Generate resource state
            let title = mote.title.length > 28 ? mote.title.substr(0, 28).trim() + ".." : mote.title.trim();
            let resource = new ResourceState("firebase://motes/" + mote.key, title, projectKey, mote.key);
            resource.bytesTotal = 50000;

            // Create asyc resource observable
            let obs = new ObservableValue<ResourceState>(observer => {
                // Fetch mote (ensure that the DB request for single mote is cached)
                this.moteService.getMote(mote.key).then(() => {
                    // Indicate request done
                    resource.bytesTransfered = 50000;
                    resource.percentage = 100;
                    resource.status = 'Cached';

                    observer.next(resource);
                    observer.complete();
                }).catch(() => observer.complete());
            });
            // Init resource state
            obs.lastVal = resource;
            return obs;
        });
    }

    private genWorldResources(world: Project, media: Media, projectKey: string): ResourceState[] {
        let list = [];
        let title = world.title.length > 28 ? world.title.substr(0, 28).trim() + ".." : world.title.trim();

        if(media.thumb.url) list.push(new ResourceState(media.thumb.url, title + ", thumb", projectKey, world.key));
        if(media.hd.url) list.push(new ResourceState(media.hd.url, title + ", image", projectKey, world.key));
        // Dont store sound and video from worlds
        //if(media.sound) list.push(new ResourceState(media.sound, title + ", sound", mote.project, mote.key));
        //if(media.video) list.push(new ResourceState(media.video, title + ", video", mote.project, mote.key));
        // if(media.square)list.push(new ResourceState(media.square,title + ", image", mote.project, mote.key));
        return list;
    }

    private genMediaRequests(motes: Mote[], abortSignal: AbortSignal, projectKey: string, fileType: MediaFileType = null): ObservableValue<ResourceState>[] {
        let requests = [];
        for(let mote of motes) {
            // Get all resources in mote and add to queue
            mote.mediaArray.forEach((media, idx) => {
                // Get all resources from this media object
                let mediaResources = this.genMediaResources(mote, media, idx + 1, projectKey, fileType);
                // Create a lazy observable fetch request for the request
                let mediaRequests = mediaResources.map(resource => {
                    return this.fetchService.fetchResource(resource, abortSignal, (rsp, res, blob) => {
                        this.offlineService.saveResource(res.url, rsp);
                    });
                });
                requests.push(...mediaRequests);
            });
        }
        return requests;
    }

    private genTileRequests(motes: Mote[]): ObservableValue<ResourceState>[] {
        let requests = [];
        for(let mote of motes) {
            // Generate tile request for mote
            let title = mote.title.length > 28 ? mote.title.substr(0, 28).trim() + ".." : mote.title.trim();
            let url = "MapTiles://" + mote.key;
            // Create new resource
            let resource = new ResourceState(url, title + ", map ", mote.project, mote.key);
            // Create lazy resource fetch request
            requests.push(this.fetchMoteMapTiles(resource));
        }
        return requests;
    }

    private genEmptyRequest(projectKey: string): ObservableValue<ResourceState> {
        let state = new ResourceState('MapTiles://' + projectKey, 'Empty', projectKey, 'NoMotes');
        state.status = 'Cached';
        state.bytesTotal = 50000;
        state.bytesTransfered = 50000;
        state.percentage = 100;

        let obs = new ObservableValue<ResourceState>(observer => {
            observer.next(state);
            observer.complete();
        });
        obs.lastVal = state;
        return obs;
    }

    private fetchMoteMapTiles(state: ResourceState): ObservableValue<ResourceState> {
        let obs = new ObservableValue<ResourceState>(observer => {
            // Fetch mote (ensure that the DB request for single mote is cached)
            this.loadMoteTiles(state, observer).then(() => observer.complete());
        });
        // Init resource state
        obs.lastVal = state;
        return obs;
    }

    private loadMoteTiles(state: ResourceState, observer: Observer<ResourceState>): Promise<any> {
        return new Promise<void>(async (resolve) => {
            let tiles = [];
            let mote = await this.moteService.getMote(state.moteKey);

            // Obtain tiles for all zoom levels of collections
            if(mote.childCount > 0) {
                tiles.push(this.loadTile(mote, state, observer, 1, 50, 10));
                tiles.push(this.loadTile(mote, state, observer, 20, 100, 600));
            }
            else {
                // Zoome close to mote (or collection)
                tiles.push(this.loadTile(mote, state, observer, 20, 50));
                tiles.push(this.loadTile(mote, state, observer, 14, 100));
            }

            await Promise.all(tiles);
            resolve();
        });
    }

    private async loadTile(mote: Mote, state: ResourceState, observer: Observer<ResourceState>, zoom: number, pct: number, maxDuration: number = 20) {
        return new Promise<void>(async resolve => {
            if(!mote) return;
            // Only allow one fly request to execute at the same time (in order wait for tiles loaded)
            await this.mapMutex.lock();
            // If resource download was cancel after put into queue
            if(state.status == 'Cancled') {
                state.percentage = 100;
                state.bytesTotal = 0;
                state.bytesTransfered = 0;
                this.mapMutex.unlock();
                observer.next(state);
                resolve();
                return;
            }

            // Load tiles of mote
            await new Promise(timeout => setTimeout(timeout, 50));
            await this.mapService.flyToMoteWhenReady(mote, zoom, 9, maxDuration);
            await this.mapService.awaitTilesLoaded();
            // Release map lock
            this.mapMutex.unlock();

            // Send tile load status update
            state.status = pct < 100 ? 'Downloading' : 'Cached';
            state.percentage = pct;
            state.bytesTotal = 50000;
            state.bytesTransfered = pct * 1000;
            observer.next(state);

            resolve();
        });
    }

    private genMediaResources(mote: Mote, media: Media, idx: number, projectKey: string, fileType: MediaFileType = null): ResourceState[] {
        let list = [];
        let title = mote.title.length > 28 ? mote.title.substr(0, 28).trim() + ".." : mote.title.trim();

        let included = (downloadType) => (!fileType || fileType == downloadType);

        if(media.thumb.url && included(MediaFileType.THUMB)) list.push(new ResourceState(media.thumb.url, title + ", thumb", projectKey, mote.key));
        if(media.hd.url && included(MediaFileType.HD)) list.push(new ResourceState(media.hd.url, title + ", image", projectKey, mote.key));
        if(media.sound.url && included(MediaFileType.SOUND)) list.push(new ResourceState(media.sound.url, title + ", sound", projectKey, mote.key));
        if(media.video.url && included(MediaFileType.VIDEO)) list.push(new ResourceState(media.video.url, title + ", video", projectKey, mote.key));
        // For now dont load square (its not used)
        // if(media.square)list.push(new ResourceState(media.square,title + ", image", mote.project, mote.key));
        return list;
    }
}
